import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import HashTag from "../components/HashTag";
import {
  Story,
  StoryHero,
  StoryNav,
  StoryOutro,
  Section,
  Figure,
  VideoFigure,
  Stat,
  StatRow,
  Card,
  Eq,
  Prose,
  Split,
  Bullets,
  Steps,
  fade,
} from "../components/story";

const IMG = "/images/projects/whale-gnss";
const VID = "/videos/whale-gnss";

const TAGS = [
  "GNSS",
  "SnapshotPositioning",
  "Doppler",
  "DeadReckoning",
  "SensorFusion",
  "KalmanFilter",
  "Whales",
  "MarineSensing",
  "WildlifeTracking",
];

const NAV = [
  ["problem", "The problem"],
  ["tag", "The tag"],
  ["approach", "Approach"],
  ["doppler", "A · Doppler"],
  ["snapshot", "B · Snapshot"],
  ["base", "C · Base & clock"],
  ["deadreckoning", "D · Dead reckoning"],
  ["results", "Results"],
  ["dives", "3-D dives"],
  ["limits", "Limits"],
  ["next", "Next steps"],
];

export default function WhaleGnssTracking() {
  return (
    <Story>
      <Helmet>
        <title>Whale Tags: GNSS Positioning &amp; Dead Reckoning | Soham Desai</title>
        <meta
          name="description"
          content="Turning a 1.8% GNSS fix yield on whale tags into a continuous 3-D track, snapshot code-phase and Doppler positioning fused with depth, IMU and camera dead reckoning. Blue whale and humpback, Monterey Bay."
        />
        <meta property="og:title" content="Whale Tags: GNSS Positioning & Dead Reckoning" />
        <meta
          property="og:description"
          content="From a 1.8% surfacing fix yield to a continuous 3-D track: snapshot GNSS, Doppler, and sensor-fusion dead reckoning on CATS biologging tags."
        />
        <meta property="og:image" content={`${IMG}/whale_tag.jpg`} />
      </Helmet>

      <StoryHero
        image={`${IMG}/whale_surface.jpg`}
        breadcrumb={{ to: "/projects/whales", label: "Whales & Marine Sensing" }}
        kicker="GNSS biologging · Monterey Bay · 2026"
        title={
          <>
            Whale Tags: GNSS Positioning
            <br />&amp; <span className="text-accent">Dead Reckoning</span>
          </>
        }
        lead={
          <>
            A whale surfaces for a few seconds and dives for an hour. Classical GNSS positions
            barely <strong className="text-foreground">1.8%</strong> of those surfacings. This
            is how snapshot code-phase, Doppler, and a pressure-constrained dead-reckoning
            filter turn that handful of locks into a continuous, physically-scaled 3-D flight
            path, for a blue whale and a humpback.
          </>
        }
        stats={[
          { value: "1.8 → 100", unit: "%", label: "surfacings with a position estimate" },
          { value: "6.9", unit: "m", label: "median between-sample error, dense regime" },
          { value: "11×", label: "more directly GNSS-positioned fixes", amber: true },
        ]}
        chips={[
          "bw260617-T4 · blue whale · 23 h",
          "mn260616-T10 · humpback · 49 h",
          "CATS biologging tags",
        ]}
      />

      <StoryNav items={NAV} />

      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* ══════════ 1 · THE PROBLEM ══════════ */}
        <Section
          id="problem"
          kicker="Why · the problem"
          title="Many surfacings, almost no fixes"
          lead="A whale is at the surface for 1–10 seconds at a time and dives for minutes to an hour. The tag sees a fleeting glimpse of sky; no continuous pseudoranges survive. The result is a track that is spatially sparse and temporally biased toward whatever the animal happened to be doing when a fix landed."
        >
          <StatRow>
            <Stat value="446" label="surfacing windows detected · blue whale T4" />
            <Stat value="7" label="positioned by accepted differential snapshot (1.6%)" amber />
            <Stat value="443" label="surfacing windows detected · humpback T10" />
            <Stat value="8" label="positioned by accepted differential snapshot (1.8%)" amber />
          </StatRow>

          <Card accent title="The bottleneck was never accuracy">
            <p>
              An earlier differential-snapshot study showed the physics was sound: held-out base
              reconstruction cut range error from ~6.4–7.2 m to ~2.1–2.3 m, a ~67% reduction.
              What was missing was the{" "}
              <em className="text-foreground not-italic font-medium">number of usable fixes</em>.
            </p>
          </Card>

          <Split>
            <Figure
              src={`${IMG}/fig_exposure_timeline.png`}
              alt="Distribution of GNSS exposure-window durations for both deployments"
              caption="Every valid-data window, measured for duration and satellite content. The distribution is brutal."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  How much sky, how often?
                </p>
              </Prose>
              <Bullets
                items={[
                  <>
                    <strong className="text-foreground">T4:</strong> 446 exposures, 348 under 1 s,
                    only 54 at ≥30 s.
                  </>,
                  <>
                    <strong className="text-foreground">T10:</strong> 443 exposures, 395 under 1
                    s, only 22 at ≥30 s.
                  </>,
                  <>
                    Median <span className="font-mono text-accent">C/N₀ ≈ 17 dB-Hz</span>, a weak,
                    wet-antenna signal.
                  </>,
                ]}
              />
              <Prose>
                <p className="text-sm text-muted-foreground">
                  This is exactly why classical positioning fails here. A Doppler arc needs a
                  long, satellite-rich pass; a code-phase snapshot needs a clean instant. Most
                  windows give neither.
                </p>
              </Prose>
            </div>
          </Split>

          <Split reverse>
            <Figure
              src={`${IMG}/fig_cno_comparison.png`}
              alt="On-whale versus dry-reference C/N0 distributions"
              caption="On-animal signal against a dry reference base station."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  The wet-antenna penalty
                </p>
              </Prose>
              <Bullets
                items={[
                  <>
                    On-whale median <span className="font-mono text-accent">C/N₀ 12–15 dB-Hz</span>{" "}
                    versus dry reference bases at{" "}
                    <span className="font-mono text-amber-300">23–31 dB-Hz</span>.
                  </>,
                  <>
                    Only <strong className="text-foreground">1.4%</strong> (T4) and{" "}
                    <strong className="text-foreground">3.4%</strong> (T10) of on-whale samples
                    clear 30 dB-Hz, against <strong className="text-foreground">56.9%</strong> at
                    the dry open-sky base.
                  </>,
                  <>
                    Wetness is a strong, though not fully isolated, causal hypothesis. A
                    controlled saline wet-layer test is the next experiment.
                  </>,
                ]}
              />
            </div>
          </Split>
        </Section>

        {/* ══════════ 2 · THE TAG ══════════ */}
        <Section
          id="tag"
          kicker="The hardware · sensors on the tag"
          title="What is actually stuck to the whale"
          lead="A CATS biologging tag is attached with suction cups and falls off on its own after a day or two. It is not a GPS collar, it is a whole instrument package: GNSS antenna, inertial sensors, a pressure sensor, and an HD camera, all recording continuously, above and below the surface."
        >
          <Split>
            <Figure
              src={`${IMG}/whale_tag.jpg`}
              alt="Yellow CATS biologging tag suction-cupped to the back of a blue whale at the surface"
              caption="The real thing: the yellow CATS tag on the back of a blue whale, Monterey Bay. The tag sits on the dorsal surface, so the GNSS antenna clears the water only for the seconds the animal is up."
            />
            <Figure
              src={`${IMG}/whale_tag2.jpg`}
              alt="CATS tag on a surfacing blue whale, wider view"
              caption="A second surfacing of the same animal. This is the entire GNSS opportunity, a wet antenna, a low elevation, and a few seconds of sky."
            />
          </Split>

          <motion.div variants={fade} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["400 Hz", "tri-axial accelerometer", "tag jiggle → speed through water"],
              ["50 Hz", "gyro + magnetometer", "roll, pitch, calibrated heading"],
              ["10 Hz", "pressure / temp / light", "depth, observed continuously"],
              ["30 fps", "1080p camera", "optical-flow speed cross-check"],
            ].map(([rate, sensor, use]) => (
              <div key={sensor} className="rounded-lg border border-border bg-surface/50 p-4">
                <div className="font-mono text-sm text-accent">{rate}</div>
                <div className="mt-1.5 text-sm font-medium text-foreground">{sensor}</div>
                <div className="mt-1 text-xs text-muted-foreground leading-snug">{use}</div>
              </div>
            ))}
          </motion.div>

          <Split>
            <Card title="Blue whale · bw260617-T4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Stat value="23.1" unit="h" label="on-animal duration" />
                <Stat value="263" unit="m" label="max dive depth" />
              </div>
              <p>
                A 117-solution RTKLIB single-point burst at tag-on, then long dives. A
                589-second camera clip enables the optical-flow cross-checks.
              </p>
            </Card>
            <Card title="Humpback · mn260616-T10">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <Stat value="49.3" unit="h" label="on-animal duration" amber />
                <Stat value="231" unit="m" label="max dive depth" amber />
              </div>
              <p>
                Ingested from raw: 96 canonical 17-minute chunks (~13.5 GB) reduced to 99,461
                one-second feature rows with a UTC index and an empirical magnetometer
                calibration. Its 17-second surfacing cadence behaves like a natural FastLoc
                experiment.
              </p>
            </Card>
          </Split>
        </Section>

        {/* ══════════ 3 · APPROACH ══════════ */}
        <Section
          id="approach"
          kicker="How we got to a solution"
          title="The tag already watches the whole dive"
          lead="The fix count is the opposite of the information available. A surfacing animal carries a depth sensor, a 400 Hz IMU and a camera that continuously observe the dive geometry. So instead of asking GNSS to work harder, we changed the question: estimate a position for every surfacing by fusing on-board 3-D motion evidence with whatever GNSS evidence exists, however partial."
        >
          <Steps
            items={[
              [
                "A",
                "Doppler tracking",
                "Use the frequency history through a satellite pass. No pseudorange needed, works on long arcs.",
              ],
              [
                "B",
                "Snapshot code phase (CTN)",
                "Coarse-time navigation from a single instant of sky. Needs only ≥4 satellites.",
              ],
              [
                "C",
                "Base stations, corrections & clock",
                "Surveyed shore references, differential corrections, and a characterised receiver clock.",
              ],
              [
                "D",
                "Dead reckoning & sensor fusion",
                "Depth + IMU + camera carry the position between surfacings; each GNSS fix is a measurement update.",
              ],
            ]}
          />
        </Section>

        {/* ══════════ 4 · DOPPLER ══════════ */}
        <Section
          id="doppler"
          kicker="Solution A · Doppler tracking"
          title="Positioning from frequency alone"
          lead="As a satellite passes overhead, its line-of-sight velocity shifts the carrier frequency: positive approaching, zero at closest approach, negative receding. The slope and curvature of that arc encode where the receiver is."
        >
          <Split>
            <VideoFigure
              src={`${VID}/anim_doppler_pass.mp4`}
              badge="animated"
              caption="A satellite pass and the Doppler arc it produces at the receiver."
            />
            <div className="space-y-5">
              <Eq>
                f<sub>meas</sub> = −(f/c)·(v<sub>sat</sub> − v<sub>rx</sub>)·ê<sub>los</sub> +
                f·drift
              </Eq>
              <Prose>
                <p>
                  No pseudorange is required, only the frequency history through the pass. That
                  matters here because the wet antenna rarely delivers a clean pseudorange, but it
                  does deliver Doppler.
                </p>
              </Prose>
            </div>
          </Split>

          <Split reverse>
            <VideoFigure
              src={`${VID}/anim_isodoppler.mp4`}
              badge="animated"
              caption="Each satellite's instantaneous Doppler defines a band of possible positions on the sea surface. Intersect enough bands and the position falls out."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  One Doppler, one band on the sea
                </p>
              </Prose>
              <Bullets
                items={[
                  <>
                    With <strong className="text-foreground">8+ satellites</strong> the
                    intersection tightens to <span className="font-mono text-accent">~1–2 km</span>.
                  </>,
                  "A running-median clock-drift calibration keeps the common-mode nuisance from stealing the geometry.",
                  "A live GLONASS FCN parse (slots R26–R28 = −6/−5/+7) fixes the L1 frequency per satellite.",
                ]}
              />
            </div>
          </Split>

          <Split>
            <VideoFigure
              src={`${VID}/anim_gate_chain.mp4`}
              badge="animated"
              caption="Each accepted fix seeds the next; infeasible jumps are rejected."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  A chained feasibility gate
                </p>
                <p>
                  A candidate fix is only admitted if it lies within a physically plausible reach
                  of the last accepted anchor. A whale cannot teleport.
                </p>
              </Prose>
              <Eq>
                gate = 2 km + v<sub>whale</sub> · Δt &nbsp;&nbsp;(v ≤ 2.5 m/s)
              </Eq>
              <Prose>
                <p>
                  Accepted Doppler fixes land{" "}
                  <strong className="text-foreground">0.3–1.9 km</strong> from the RTKLIB
                  reference; jumps demanding super-whale speed are thrown out. The reported r₉₅
                  bounded the true error in 8 of 9 matched solves.
                </p>
              </Prose>
            </div>
          </Split>

          <Figure
            src={`${IMG}/fig_doppler_physics_T4.png`}
            alt="Measured Doppler arcs and residuals for the blue whale deployment"
            caption="Measured Doppler physics on the blue whale, T4. The arcs are there; they are just short, noisy, and clock-contaminated compared to a dry receiver."
          />
        </Section>

        {/* ══════════ 5 · SNAPSHOT ══════════ */}
        <Section
          id="snapshot"
          kicker="Solution B · snapshot code phase"
          title="The second observable: fractional code phase"
          lead="One C/A code period is 1 ms, which is 299.79 km of range. The receiver logs only the fractional part; the integer millisecond, i.e. which code period, is unknown. Coarse-time navigation resolves that ambiguity from a single instant of sky."
        >
          <Split>
            <div className="space-y-5">
              <Prose>
                <p>
                  <strong className="text-foreground">Coarse-time navigation</strong> (van
                  Diggelen): with GPS time known to ±1 minute and ≥4 satellites, the
                  integer-millisecond ambiguity resolves into a metre-scale snapshot fix, from one
                  instant, no tracking loop, no continuous lock.
                </p>
              </Prose>
              <Eq>frac. code phase + gpsTOW (±1 min) + ≥4 SV → metre-class fix</Eq>
              <Prose>
                <p className="text-sm text-muted-foreground">
                  GPS-only (GLONASS FDMA and QZSS excluded). Three-satellite solves were uniformly
                  false locks and are discarded. Validated at{" "}
                  <strong className="text-foreground">8–13 m median</strong> against surveyed base
                  stations.
                </p>
              </Prose>
            </div>
            <Figure
              src={`${IMG}/fig_skyplot.png`}
              alt="Sky plot of satellite geometry during accepted snapshot solves"
              caption="Satellite geometry during accepted snapshots. Whatever is above the horizon in that one second is the whole budget."
            />
          </Split>

          <Split reverse>
            <VideoFigure
              src={`${VID}/anim_method_race.mp4`}
              badge="animated"
              caption="Doppler and CTN run on the same surfacing windows."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  Doppler vs CTN, then fused
                </p>
                <p>Run on the same windows, the two methods turn out to be complementary:</p>
              </Prose>
              <Bullets
                items={[
                  <>
                    <strong className="text-foreground">Doppler</strong> needs a long arc (≥30 s,
                    ≥8 SV) → a coarse ~1–2 km position.
                  </>,
                  <>
                    <strong className="text-foreground">CTN</strong> needs only a snapshot (≥4 SV)
                    → a tight ~10–20 m position.
                  </>,
                  <>
                    Inverse-variance <strong className="text-foreground">fusion</strong>: CTN where
                    a clean snapshot exists, Doppler fills the gaps.
                  </>,
                ]}
              />
            </div>
          </Split>

          <Split>
            <Figure
              src={`${IMG}/fig_method_compare.png`}
              alt="Comparison of Doppler and snapshot CTN accuracy on the same surfacings"
              caption="Where both methods solve, the 0.6–2.2 km separation is dominated by Doppler error."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  Snapshot CTN wins on-animal
                </p>
              </Prose>
              <div className="grid grid-cols-2 gap-3">
                <Stat value="10" unit="m" label="T4 CTN vs reference (tight overlap)" />
                <Stat value="26" unit="m" label="T10 CTN median (n=29, 2–152 m)" amber />
              </div>
              <Prose>
                <p>
                  Accepted gated fixes: <strong className="text-foreground">5</strong> (T4) and{" "}
                  <strong className="text-foreground">32</strong> (T10). Doppler still earns its
                  place by filling bursts CTN cannot solve.
                </p>
                <p className="text-sm text-muted-foreground">
                  Inverse-variance fused track: T4 lands within 1.1 km of the independent
                  focal-follow boat; T10 median 19 m. Both methods are ultimately data-limited on
                  the animal, not algorithm-limited.
                </p>
              </Prose>
            </div>
          </Split>
        </Section>

        {/* ══════════ 6 · BASE & CLOCK ══════════ */}
        <Section
          id="base"
          kicker="Solution C · base stations, corrections & clock"
          title="Knowing what a good signal looks like"
          lead="Two shore base stations were surveyed against CORS station P231 and agree to within 1–5 m of the documented coordinates. They give a trustworthy differential reference, and, just as importantly, a control: what this receiver does when the antenna is dry."
        >
          <Split>
            <Figure
              src={`${IMG}/fig_base_doppler_patterns.png`}
              alt="Smooth one-hour Doppler arcs recorded at the dry base station"
              caption="Dry-base control T7: smooth one-hour Doppler arcs, |df/dt| median 0.40 Hz/s. This is the clean signal the wet antenna never delivers."
            />
            <div className="space-y-5">
              <Bullets
                items={[
                  <>
                    Differential code-phase reduced held-out base range error to{" "}
                    <span className="font-mono text-accent">~2.1 m</span>.
                  </>,
                  "RTKLIB is retained only for the base survey: valid ≥4-SV RAWX epochs on the animal exist only in the first minutes after tag-on, and none afterwards.",
                  "The dry-base arcs are what let us tell 'the method is wrong' apart from 'the antenna was underwater'.",
                ]}
              />
            </div>
          </Split>

          <Figure
            src={`${IMG}/clock_stable.png`}
            alt="Receiver clock frequency drift over both deployments after running-median calibration"
            caption="Receiver clock stability across both deployments."
          />
          <Split>
            <Prose>
              <p className="font-display text-xl font-semibold text-foreground">
                The clock that behaves
              </p>
              <p>
                The receiver's frequency drift is a nuisance, but a well-behaved one. A
                running-median calibration across the whole deployment reduces the residual to a
                few parts per billion.
              </p>
              <p>
                Split-half held-out tests confirm the calibration is{" "}
                <strong className="text-foreground">not over-fit</strong>: out-of-sample errors
                match in-sample (T10: 1107 vs 1115 m). Frequency stability is separated cleanly
                from absolute time sync.
              </p>
            </Prose>
            <div className="grid grid-cols-2 gap-3 self-start">
              <Stat value="5.6" unit="ppb" label="T4 residual drift (0.159 ppm)" />
              <Stat value="1.7" unit="ppb" label="T10 residual drift (0.824 ppm)" amber />
            </div>
          </Split>
        </Section>

        {/* ══════════ 7 · DEAD RECKONING ══════════ */}
        <Section
          id="deadreckoning"
          kicker="Solution D · dead reckoning & sensor fusion"
          title="Why dead reckoning at all?"
          lead="Even after Doppler and snapshot CTN lift the directly-positioned count by 11×, the whale is still underwater for the overwhelming majority of the deployment, and that is precisely where the biology happens. Feeding, diving, travelling: none of it is at the surface. A GNSS-only track answers 'where did it come up' and never 'where did it go'."
        >
          <Card accent title="The premise">
            <p>
              Each accepted GNSS fix becomes an <em>anchor</em>. Between anchors, the tag's own
              sensors say how fast the animal was moving and in which direction, so the position
              can be carried forward. Every later surfacing is then a chance to correct the
              accumulated drift. Pressure observes depth continuously, so the vertical coordinate
              is never guessed at all.
            </p>
          </Card>

          <Figure
            src={`${IMG}/fusion_architecture.png`}
            alt="Block diagram of the pressure-constrained 3-D sensor fusion architecture"
            caption="How it is done. Accelerometer and magnetometer give 3-D attitude; the 10–90 Hz acceleration RMS ('tag jiggle') gives speed through water; together they form a horizontal dead-reckoning velocity. A causal Kalman filter takes that velocity as the process input and GNSS surfacings as measurement updates; an RTS smoother makes a retrospective pass. Pressure supplies the vertical state directly."
          />

          <Card title="Observability by design">
            <p>
              Pressure observes the vertical coordinate{" "}
              <em className="text-foreground not-italic font-medium">continuously</em>; GNSS
              corrects the horizontal state only at surfacings. Acceleration is{" "}
              <strong className="text-foreground">never doubly integrated</strong>: underwater
              current and inertial bias are unobservable here, so a strapdown INS would invent
              false precision. The filter is deliberately built to only claim what the sensors
              actually see.
            </p>
          </Card>

          <Eq>
            x = [E, N, c<sub>E</sub>, c<sub>N</sub>] &nbsp;·&nbsp; u = s·cos p ·[sin h, cos h]{" "}
            &nbsp;·&nbsp; GNSS updates E/N at surfacings
          </Eq>
          <Prose>
            <p className="text-sm text-muted-foreground">
              The Kalman state is horizontal east/north plus two slowly-varying velocity-error
              terms that absorb current and low-frequency heading/speed bias. Dead-reckoned
              velocity is the process input; pressure supplies the vertical.
            </p>
          </Prose>

          <Split>
            <Figure
              src={`${IMG}/T4_speed_model.png`}
              alt="Jiggle-based speed-through-water model and its cross-validated fit"
              caption="Speed from tag jiggle: 10–90 Hz acceleration RMS regressed against depth-rate/pitch labels during steep dives."
            />
            <div className="space-y-5">
              <Prose>
                <p className="font-display text-xl font-semibold text-foreground">
                  Speed from tag jiggle
                </p>
                <p>
                  Following the CATS workflow, the flow noise across the tag body is a proxy for
                  speed through water. During steep dives, depth rate and pitch give a physical
                  label to regress against.
                </p>
              </Prose>
              <Eq>ŝ = |ḋ| / |sin p| &nbsp;→&nbsp; Huber fit on jiggle + VeDBA</Eq>
              <div className="grid grid-cols-2 gap-3">
                <Stat value="0.47" unit="m/s" label="T4 blocked-CV MAE (R²=0.24)" />
                <Stat value="0.35" unit="m/s" label="T10 blocked-CV MAE (R²=0.15)" amber />
              </div>
              <Prose>
                <p className="text-sm text-muted-foreground">
                  Purged, contiguous-block cross-validation, no adjacent-sample leakage. Jiggle
                  weakly but honestly constrains absolute speed, and the low R² is reported rather
                  than hidden, because it is what sets the drift budget below.
                </p>
              </Prose>
            </div>
          </Split>

          <VideoFigure
            src={`${VID}/bluewhale_T4_optical_flow_h264.mp4`}
            badge="real footage · Lucas–Kanade"
            caption="Optical flow as an independent speed cross-check, on real tag-camera footage from the blue whale. Persistent features are tracked with forward/backward rejection, RANSAC global motion, and a gyro-derived rotation correction."
          />
          <Split>
            <Figure
              src={`${IMG}/T4_optical_flow_explainer.png`}
              alt="Persistent Lucas-Kanade feature tracks in tag camera footage"
              caption="The tracked features behind the video above."
            />
            <Prose>
              <p>
                Under purged blocked validation the guard{" "}
                <strong className="text-foreground">retained jiggle</strong> as the applied speed,
                so optical flow enters as a cross-check, not the primary estimator. Pixel motion is
                rotation-compensated but is not independent metric ground truth, there is no scene
                range and no lens intrinsics.
              </p>
              <p className="text-sm text-muted-foreground">
                This is the sort of result that is tempting to bury. It is more useful as a bound:
                the camera agrees with jiggle well enough to trust the jiggle, and not well enough
                to replace it.
              </p>
            </Prose>
          </Split>
        </Section>

        {/* ══════════ 8 · RESULTS ══════════ */}
        <Section
          id="results"
          kicker="Validation & results"
          title="Validated against the anchors it holds out"
          lead="Accepted surfacings are solved deterministically as dead-reckoning anchors. The first inter-anchor leg calibrates the tag-mounting yaw; every later surfacing is held out until prediction. Only the causal pre-update state is scored as a live outage error, the RTS smoother uses future fixes and must never be read as live accuracy."
        >
          <Split>
            <Figure
              src={`${IMG}/T10_causal_validation.png`}
              alt="Causal validation of four dead-reckoning baselines on the humpback"
              caption="Four causal baselines compared: last-position, extrapolated GNSS velocity, jiggle dead reckoning, and the DR/current Kalman filter. An independent RAWX single-point solution provides an external cross-validation set."
            />
            <Figure
              src={`${IMG}/fig1_between_samples.png`}
              alt="Held-out between-sample position error in the dense surfacing regime"
              caption="In the humpback's FastLoc-like window (17-second cadence, 49 tier-A fixes), each interior surfacing is held out and reconstructed from its bracketing neighbours."
            />
          </Split>

          <StatRow cols={3}>
            <Stat value="6.9" unit="m" label="median held-out error (n=47)" />
            <Stat value="18.3" unit="m" label="90th percentile" />
            <Stat value="22.4" unit="m" label="independent SPP cross-validation median" amber />
          </StatRow>
          <Prose>
            <p className="text-sm text-muted-foreground">
              The 22.4 m SPP cross-validation figure itself includes the reference's own error and
              up to 10 s of whale motion. The 6.9 m number is the honest basis for
              "metre-to-few-metre between samples", and it holds{" "}
              <em className="text-foreground not-italic font-medium">
                where surfacings are frequent
              </em>
              , not as an unconditional guarantee.
            </p>
          </Prose>

          <motion.div
            variants={fade}
            className="rounded-lg border border-accent/30 bg-surface/50 p-5"
          >
            <h3 className="font-display text-base font-semibold text-foreground mb-4">
              Surfacings positioned vs estimated, humpback T10
            </h3>
            <div className="grid gap-3 sm:grid-cols-4">
              {[
                ["detected", "443", "surfacings", "text-foreground"],
                ["GNSS snapshot only", "8", "positioned (1.8%)", "text-muted"],
                ["+ snapshot CTN & Doppler", "90", "positioned (11×)", "text-amber-300"],
                ["+ sensor fusion", "443", "estimated (~100%, 55×)", "text-accent"],
              ].map(([stage, n, sub, colour]) => (
                <div key={stage} className="rounded-lg border border-border bg-background/40 p-4">
                  <div className="font-mono text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                    {stage}
                  </div>
                  <div className={`mt-2 font-display text-2xl font-semibold ${colour}`}>{n}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-muted leading-relaxed">
              Snapshot and Doppler lift the directly GNSS-positioned count from 8 to 90, an 11×
              gain. Sensor fusion then estimates a position for{" "}
              <strong className="text-foreground">every</strong> surfacing, taking coverage from
              1.8% to ~100%, over 50× the GNSS-only yield.
            </p>
          </motion.div>

          <Figure
            src={`${IMG}/T10_surfacing_kalman_validation.png`}
            alt="Kalman filter and RTS smoother reconstruction across all humpback surfacings"
            caption="The causal Kalman filter plus RTS smoother knit the deterministic anchors and the confirmed validation set into a continuous estimate."
          />

          <Figure
            src={`${IMG}/fig2_track_overview_both.png`}
            alt="Whole-deployment fused tracks for the blue whale and the humpback"
            caption="Fused retrospective (RTS) tracks with accepted GNSS surfacing anchors. T4: 6 anchors (1 candidate rejected by the depth/continuity gate); T10: 19 anchors. Coarse tier-B fixes (400 m declared uncertainty) sit off the smoothed path, correctly down-weighted. Horizontal positions between surfacings are model estimates; depth is observed."
          />

          <motion.div variants={fade} className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <caption className="text-left text-xs text-muted-foreground pb-3">
                Every number read directly from the pipeline CSV/JSON artefacts, measured results,
                explicitly separated from forward-looking system targets.
              </caption>
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2.5 pr-4 text-muted-foreground font-normal">Metric</th>
                  <th className="py-2.5 pr-4 font-medium text-foreground">Blue whale T4</th>
                  <th className="py-2.5 font-medium text-foreground">Humpback T10</th>
                </tr>
              </thead>
              <tbody className="text-muted">
                {[
                  ["On-animal duration (h)", "23.1", "49.3"],
                  ["Accepted surfacing anchors", "6", "19"],
                  ["QC GNSS surfacing fixes", "burst", "90 (80 A / 10 B)"],
                  ["Independent SPP cross-val (m, median)", "n/a", "22.4"],
                  ["Dense between-sample error (m, median)", "n/a", "6.9"],
                  ["90th pct / max (m)", "n/a", "18.3 / 31.7"],
                  ["Max depth (m)", "263", "231"],
                  ["Cumulative vertical (km)", "45.0", "41.5"],
                  ["Speed-model MAE (m/s)", "0.47", "0.35"],
                  ["Hold-time to 100 m / 1 km (s)", "273 / 2727", "24 / 243"],
                ].map(([metric, t4, t10]) => (
                  <tr key={metric} className="border-b border-border/50">
                    <td className="py-2.5 pr-4">{metric}</td>
                    <td className="py-2.5 pr-4 font-mono text-xs">{t4}</td>
                    <td className="py-2.5 font-mono text-xs">{t10}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </Section>

        {/* ══════════ 9 · 3D DIVES ══════════ */}
        <Section
          id="dives"
          kicker="3-D reconstruction"
          title="Tracking the dive, not just the surfacing"
          lead="Pressure makes depth strongly observable at every sample. Fused with posture and speed, the engine reconstructs the full three-dimensional flight path of each dive, the part of the animal's day that GNSS can never see."
        >
          <Split>
            <div className="space-y-4">
              <VideoFigure
                src={`${VID}/bluewhale_T4_3d_flight_h264.mp4`}
                badge="blue whale T4"
                caption="Colour encodes pressure-derived ascent / descent. The vertical trajectory is directly sensor-backed; the east/north shape is the GNSS-anchored dead-reckoning estimate. Cumulative vertical travel over the deployment: 45.0 km."
              />
              <div className="grid grid-cols-2 gap-3">
                <Stat value="263" unit="m" label="max depth reached" />
                <Stat value="738" unit="s" label="deepest single dive" />
              </div>
            </div>
            <div className="space-y-4">
              <VideoFigure
                src={`${VID}/humpback_T10_3d_flight.mp4`}
                badge="humpback T10"
                caption="The same pressure-constrained fusion on the humpback, depth from IMU here, with camera fusion deferred. Cumulative vertical travel: 41.5 km over 49 hours."
              />
              <div className="grid grid-cols-2 gap-3">
                <Stat value="231" unit="m" label="max depth reached" amber />
                <Stat value="527" unit="s" label="deepest single dive" amber />
              </div>
            </div>
          </Split>

          <Split>
            <Figure
              src={`${IMG}/T4_trajectory_3d.png`}
              alt="Full retrospective 3-D pseudo-track with horizontal uncertainty encoding"
              caption="Full retrospective 3-D pseudo-track with horizontal-uncertainty encoding, and a metric-scale view of the deepest surface-bounded dive."
            />
            <Figure
              src={`${IMG}/T4_attitude_3d.png`}
              alt="Estimated body axes with roll, pitch and heading during the deepest dive"
              caption="Estimated forward–right–down body axes with roll, pitch and heading/yaw during the deepest dive. Body attitude and movement direction need not coincide exactly."
            />
          </Split>

          <motion.p
            variants={fade}
            className="font-display text-xl font-semibold text-foreground"
          >
            Watching the track assemble in real time
          </motion.p>
          <Split>
            <VideoFigure
              src={`${VID}/bluewhale_T4_10min_tracking.mp4`}
              badge="10 min · T4"
              caption="Blue whale T4. The opening code-phase burst, then a 180 m dive, each snapshot dropped at its true time with a 95% uncertainty ring, synced to the observed dive profile."
            />
            <VideoFigure
              src={`${VID}/humpback_T10_10min_tracking.mp4`}
              badge="10 min · T10"
              caption="Humpback T10. 30 surfacings at 17-second cadence in the surface-active window, the dense regime where between-sample error is 6.9 m."
            />
          </Split>
        </Section>

        {/* ══════════ 10 · LIMITS ══════════ */}
        <Section
          id="limits"
          kicker="Honesty · what this does not claim"
          title="The honest limit: kilometre-scale drift across long dives"
          lead="Where surfacings are frequent, snapshot-plus-fusion estimates each surfacing to single-digit metres and bridges the seconds-scale gaps at metre-to-tens-of-metres. It does not, on this data, deliver an unconditional 1–2 m guarantee across long dive outages."
        >
          <StatRow cols={3}>
            <Stat
              value="1"
              unit="km"
              label="humpback T10 error envelope after only 243 s without a fix"
            />
            <Stat value="1" unit="km" label="blue whale T4 envelope after 2727 s (~45 min)" amber />
            <Stat
              value="3.3"
              unit="km"
              label="median causal drift across T4's sparse multi-hour dive gaps"
            />
          </StatRow>

          <Split>
            <Figure
              src={`${IMG}/fig3_error_vs_gap.png`}
              alt="Held-out position error as a function of dive-outage duration"
              caption="Held-out error grows with dive-outage duration. The dense regime lives in the metre-to-tens-of-metres band; sparse multi-hour outages drift to kilometres."
            />
            <Figure
              src={`${IMG}/fig4_hold_time_both.png`}
              alt="Empirical position hold-time curves for both deployments"
              caption="Empirical position hold-time: T10 reaches a 100 m envelope after 24 s and 1 km after 243 s; T4 after 273 s and 2727 s. A deployment-specific envelope, not a navigation-integrity guarantee."
            />
          </Split>

          <Card title="What is genuinely unresolved">
            <Bullets
              items={[
                "Between sparse surfacings the inertial dead reckoning drifts to kilometre scale.",
                "Jiggle and heading only weakly constrain absolute velocity (R² ≈ 0.15–0.24).",
                "Underwater current is unobserved; the T10 magnetometer calibration is an empirical field fit.",
                "Post-burst points have no independent position truth, and the RTS smoother uses future fixes.",
              ]}
            />
            <p className="pt-2 text-sm text-muted-foreground">
              Extending causal hold-time requires a{" "}
              <strong className="text-foreground">new independent horizontal observable</strong>,
              not a more complex filter. That framing is what drives the next steps.
            </p>
          </Card>
        </Section>

        {/* ══════════ 11 · NEXT STEPS ══════════ */}
        <Section
          id="next"
          kicker="Next steps"
          title="Where this goes next"
          lead="Each step attacks a measured limit: stronger signal, tighter observables, more frequent fixes, and steadier timing."
        >
          <motion.div variants={fade} className="grid gap-4 sm:grid-cols-2">
            {[
              [
                "Better antenna · L1 + L5 wideband",
                "A higher-gain, dual-band L1/L5 antenna widens the capture bandwidth, lifting wet-surface C/N₀ and adding a second frequency for more robust, ionosphere-resistant snapshots.",
              ],
              [
                "Raw carrier-phase output",
                "These logs carried only code phase and Doppler. Recording and analysing raw carrier phase opens far tighter, sub-metre observables once the cycle ambiguities can be worked.",
              ],
              [
                "LEO + GEO augmentation",
                "LEO and GEO signals add fast-changing geometry and always-in-view sources, shortening the exposure a fix needs and turning today's sub-second glimpses into solvable arcs.",
              ],
              [
                "Improved TCXO",
                "A better temperature-compensated oscillator tightens receiver-clock stability, shrinking the drift the fusion engine has to bridge between surfacings.",
              ],
              [
                "Controlled wet-antenna experiment",
                "A saline wet-layer bench test to isolate the wetness hypothesis from mounting, elevation, and multipath, so the C/N₀ penalty can be attributed rather than inferred.",
              ],
              [
                "A new horizontal observable",
                "Acoustic ranging or a current model would attack the one limit a better filter cannot: unobserved horizontal motion during long dives.",
              ],
            ].map(([title, body]) => (
              <div
                key={title}
                className="rounded-lg border border-border bg-surface/50 p-5 hover:border-accent/30 transition-colors"
              >
                <h3 className="font-display text-base font-semibold text-foreground mb-2">
                  {title}
                </h3>
                <p className="text-sm text-muted leading-relaxed">{body}</p>
              </div>
            ))}
          </motion.div>
        </Section>

        <StoryOutro
          title="From a 1.8% fix yield to a continuous 3-D track"
          tags={TAGS}
          TagComponent={HashTag}
          backTo="/projects/whales"
          backLabel="More from Whales & Marine Sensing"
        >
          <p className="mt-4 text-lg">
            Treating every surfacing as an opportunistic snapshot, and fusing depth, IMU and
            camera to carry the position between them, turns a handful of GNSS locks into a
            physically-scaled 3-D flight path for both a blue whale and a humpback. In the dense
            regime the between-sample error is a measured{" "}
            <strong className="text-foreground">6.9 m median</strong>.
          </p>
          <p>
            The remaining gap to metre-level everywhere is set by surfacing frequency and snapshot
            quality, exactly the quantities the LEO roadmap is built to improve.
          </p>
        </StoryOutro>
      </div>
    </Story>
  );
}
